import React from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import AddCircleIcon from "@/assets/icons/misc/add-circle-icon.tsx"
import CloseCrossIcon from "@/assets/icons/misc/close-cross-icon.tsx"
import RemoveCircleIcon from "@/assets/icons/misc/remove-circle-icon.tsx"
import CircleTickIcon from "@/assets/icons/task/circle-tick-icon.tsx"
import CropIcon from "@/assets/svg/crop-planting-icon.svg?react"
import { type RenderFunctionDrawerItemComponentProps } from "@/components/render-function-drawer/render-function-drawer.tsx"
import Skeleton from "@/components/skeleton/skeleton.tsx"
import { useGetCropPlantingQuery } from "@/features/crops/api/crops-planting-api.ts"
import { CROP_PLANTING_VIEW } from "@/features/crops/routes/route-list.ts"
import { type CropPlanting } from "@/features/crops/types/crop-planting.ts"
import styles from "@/features/tasks/components/associated-list/associated-list.module.css"
import { cn } from "@/utils/styles.ts"

export const AssociatedCropPlantingCard: React.FC<
  RenderFunctionDrawerItemComponentProps
> = ({ id, onDelete, isSelected, onSelect }) => {
  const { data, isLoading } = useGetCropPlantingQuery(id)
  const { t } = useTranslation()
  const navigate = useNavigate()

  const navigateTo = () => {
    navigate(CROP_PLANTING_VIEW.replace(":cropPlantingId", id))
  }

  const handleClick = () => {
    onSelect && onSelect()
    !onSelect && navigateTo()
  }

  const handleDelete = (event: React.MouseEvent) => {
    event.stopPropagation()
    onDelete && onDelete(id)
  }

  if (isLoading || !data) {
    return (
      <Skeleton
        className={
          "flex items-center justify-between gap-2 rounded-[10px] border border-gray-200 bg-[var(--light-grey)] p-2 text-neutral-700"
        }
      />
    )
  }

  return (
    <div
      onClick={handleClick}
      className={cn(
        "flex items-center justify-between gap-2 rounded-[10px] border border-gray-200 bg-[var(--light-grey)] p-2 text-neutral-700",
        isSelected && "border border-brown-500 bg-white",
      )}
    >
      <div
        className={cn(
          "grid w-[70%] grid-cols-[30px_auto] items-center gap-2",
          onSelect && "w-full grid-cols-[30px_30px_auto]",
        )}
      >
        {onSelect && (
          <>
            {isSelected ? (
              <CircleTickIcon />
            ) : (
              <div
                className={
                  "border-dark-grey box-border h-[24px] w-[24px] rounded-full border bg-[var(--background-color)] pr-3"
                }
              />
            )}
          </>
        )}
        <div
          className={cn(
            "grid h-[30px] w-[30px] place-content-center rounded-[4px] bg-gray-600 text-white opacity-60",
            isSelected && "bg-brown-500",
          )}
        >
          <CropIcon
            className={cn(
              "h-[30px] w-[30px] rounded bg-gray-600 p-1 text-white",
              isSelected && "bg-brown-500",
            )}
          />
        </div>
        <div className={"flex w-[70%] flex-col"}>
          <div className={"truncate text-[14px]"}>{t(data?.crop_name)}</div>
          <div className={"truncate text-neutral-700"}>{data?.parcel_name}</div>
        </div>
      </div>
      {onDelete && (
        <div onClick={handleDelete}>
          <CloseCrossIcon className={"text-neutral-500"} />
        </div>
      )}
    </div>
  )
}

interface Props {
  cropPlantings: CropPlanting[]
  selectedValues: string[]
  onDelete: (cropPlanting: string[]) => void
}

export const AssociatedCropPlantingList: React.FC<Props> = ({
  cropPlantings,
  selectedValues,
  onDelete,
}) => {
  const { t } = useTranslation()
  const selectedCropPlantings = cropPlantings.filter((parcel) =>
    selectedValues.includes(parcel.id),
  )
  const [listLimit, setListLimit] = React.useState(3)
  const expandList = () => setListLimit(selectedCropPlantings.length)
  const collapseList = () => setListLimit(3)
  const list = selectedCropPlantings.slice(0, listLimit)

  const handleDelete = (cropPlantingId: string) => {
    const newCropPlantings = selectedCropPlantings.filter(
      (cropPlanting) => cropPlanting.id !== cropPlantingId,
    )
    onDelete(newCropPlantings.map((q) => q.id))
  }

  return (
    <div className={"flex flex-col gap-1"}>
      {list.map((parcel, index) => (
        <AssociatedCropPlantingCard
          id={parcel.id}
          key={index}
          onDelete={handleDelete}
        />
      ))}
      {selectedCropPlantings.length > listLimit && (
        <i className={styles["more-items"]} onClick={expandList}>
          <AddCircleIcon size={20} /> {t("seeOtherAssociated")}
        </i>
      )}
      {selectedCropPlantings.length === listLimit &&
        selectedCropPlantings.length > 3 && (
          <i className={styles["more-items"]} onClick={collapseList}>
            <RemoveCircleIcon size={20} /> {t("hideOtherAssociated")}
          </i>
        )}
    </div>
  )
}
